import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../context/LanguageContext';
import {
    Globe, Plus, Trash2, Edit, X, Save, ExternalLink, Loader2,
    Eye, Sparkles, ToggleLeft, ToggleRight, RefreshCw, Copy, Check,
    Mail, Phone, MapPin, Tag, Facebook, Linkedin, Twitter, Youtube, Instagram,
    AlertTriangle, ShieldAlert, Camera
} from 'lucide-react';
import { getMyWebsites, createWebsite, updateWebsite, updateWebsiteData, deleteWebsite, translateWebsiteToAllLanguages, generateSnapshot, isGeminiAvailable } from '../../services/websitesService';
import { getStoredUser as getUser } from '../../context/AuthContext';
import { checkUrlContent, isModerationAvailable } from '../../services/contentModerationService';
import PageHeader from '../ui/PageHeader';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import ConfirmModal from '../ui/ConfirmModal';
import { CustomDropdown } from '../ui';
import WebsiteDetail from './WebsiteDetail';

const WebsitesPage = () => {
    const { t, language } = useTranslation();
    const navigate = useNavigate();
    const user = getUser();

    // State
    const [websites, setWebsites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingWebsite, setEditingWebsite] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [analyzingId, setAnalyzingId] = useState(null);
    const [copiedUrl, setCopiedUrl] = useState(null);
    const [moderationError, setModerationError] = useState(null);
    const [checkingContent, setCheckingContent] = useState(false);
    const [generatingSnapshotId, setGeneratingSnapshotId] = useState(null);
    const [selectedWebsiteId, setSelectedWebsiteId] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        protocol: 'https://',
        domain: '',
        title: '',
        // Multilingual fields (editable in current language)
        editTitle: '',
        editDescription: '',
        editKeywords: '',
        editCategory: '',
        isPublic: true,
        isActive: true
    });

    // Load data
    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const data = await getMyWebsites();
            setWebsites(data);
        } catch (error) {
            console.error('Error loading websites:', error);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            protocol: 'https://',
            domain: '',
            title: '',
            editTitle: '',
            editDescription: '',
            editKeywords: '',
            editCategory: '',
            isPublic: true,
            isActive: true
        });
        setEditingWebsite(null);
    };

    const handleOpenForm = (website = null) => {
        if (website) {
            setEditingWebsite(website);
            const url = website.url || '';
            const protocol = url.startsWith('http://') ? 'http://' : 'https://';
            const domain = url.replace(/^https?:\/\//, '');
            // Get current language code
            const langCode = language?.substring(0, 2) || 'fr';
            // Load multilingual fields for current language
            setFormData({
                protocol,
                domain,
                title: website.title || '',
                editTitle: website[`title_${langCode}`] || '',
                editDescription: website[`description_${langCode}`] || '',
                editKeywords: website[`keywords_${langCode}`] || '',
                editCategory: website[`category_${langCode}`] || '',
                isPublic: website.isPublic ?? true,
                isActive: website.isActive ?? true
            });
        } else {
            resetForm();
        }
        setShowForm(true);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        resetForm();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setModerationError(null);

        if (!formData.domain.trim()) {
            alert(t.websites?.urlRequired || 'L\'URL est requise');
            return;
        }

        if (!editingWebsite && !formData.editTitle.trim()) {
            alert(t.websites?.titleRequired || 'Le titre est requis');
            return;
        }

        // Build full URL from protocol and domain
        const url = `${formData.protocol}${formData.domain.trim()}`;

        setSaving(true);

        // Check content moderation for new websites
        if (!editingWebsite && isModerationAvailable()) {
            setCheckingContent(true);
            try {
                const moderationResult = await checkUrlContent(url);
                if (!moderationResult.safe) {
                    setModerationError(moderationResult.reason || 'Ce site contient du contenu inapproprié et ne peut pas être ajouté.');
                    setSaving(false);
                    setCheckingContent(false);
                    return;
                }
            } catch (error) {
                console.error('Error checking content:', error);
            }
            setCheckingContent(false);
        }

        try {
            let result;
            const langCode = language?.substring(0, 2) || 'fr';

            if (editingWebsite) {
                // UPDATE: Step 1 - update basic fields
                result = await updateWebsite(editingWebsite.id, {
                    isPublic: formData.isPublic,
                    isActive: formData.isActive
                });

                if (result.success) {
                    // Step 2 - save multilingual fields for current language
                    const multilingualData = {
                        [`Title_${langCode}`]: formData.editTitle,
                        [`Description_${langCode}`]: formData.editDescription,
                        [`Keywords_${langCode}`]: formData.editKeywords,
                        [`Category_${langCode}`]: formData.editCategory
                    };
                    await updateWebsiteData(editingWebsite.id, multilingualData);

                    handleCloseForm();
                    await loadData();

                    // Background: translate to 7 other languages
                    if (isGeminiAvailable() && (formData.editTitle || formData.editDescription)) {
                        const sourceContent = {
                            title: formData.editTitle,
                            description: formData.editDescription,
                            keywords: formData.editKeywords,
                            category: formData.editCategory
                        };
                        translateWebsiteToAllLanguages(editingWebsite.id, sourceContent, langCode)
                            .then(() => loadData())
                            .catch(err => console.error('Background translation error:', err));
                    }
                } else {
                    alert(result.message);
                }
            } else {
                // CREATE: Step 1 - create website with basic fields
                result = await createWebsite({
                    url,
                    title: formData.editTitle,
                    isPublic: formData.isPublic
                });

                if (result.success && result.data?.id) {
                    const websiteId = result.data.id;

                    // Step 2 - save multilingual fields for current language
                    const multilingualData = {
                        [`Title_${langCode}`]: formData.editTitle,
                        [`Description_${langCode}`]: formData.editDescription,
                        [`Keywords_${langCode}`]: formData.editKeywords,
                        [`Category_${langCode}`]: formData.editCategory
                    };
                    await updateWebsiteData(websiteId, multilingualData);

                    handleCloseForm();
                    await loadData();

                    // Generate snapshot in background
                    setGeneratingSnapshotId(websiteId);
                    generateSnapshot(websiteId, url)
                        .then(() => loadData())
                        .catch(err => console.error('Error generating snapshot:', err))
                        .finally(() => setGeneratingSnapshotId(null));

                    // Background: translate to 7 other languages
                    if (isGeminiAvailable() && (formData.editTitle || formData.editDescription)) {
                        const sourceContent = {
                            title: formData.editTitle,
                            description: formData.editDescription,
                            keywords: formData.editKeywords,
                            category: formData.editCategory
                        };
                        translateWebsiteToAllLanguages(websiteId, sourceContent, langCode)
                            .then(() => loadData())
                            .catch(err => console.error('Background translation error:', err));
                    }
                } else {
                    alert(result.message);
                }
            }
        } catch (error) {
            console.error('Error saving website:', error);
            alert('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteConfirm) return;

        try {
            const result = await deleteWebsite(deleteConfirm.id);
            if (result.success) {
                await loadData();
            } else {
                alert(result.message);
            }
        } catch (error) {
            console.error('Error deleting website:', error);
        } finally {
            setDeleteConfirm(null);
        }
    };

    const handleTogglePublic = async (website) => {
        try {
            await updateWebsite(website.id, { isPublic: !website.isPublic });
            await loadData();
        } catch (error) {
            console.error('Error toggling website:', error);
        }
    };

    const handleAnalyze = async (website, triggerTranslation = true) => {
        if (!isGeminiAvailable()) {
            alert(t.websites?.aiNotAvailable || 'L\'analyse IA n\'est pas disponible');
            return;
        }

        const langCode = language?.substring(0, 2) || 'fr';
        setAnalyzingId(website.id);
        try {
            const result = await analyzeWebsiteWithAI(website.id, website.url, langCode);
            if (result.success) {
                await loadData();

                // Trigger background translation to other languages
                if (triggerTranslation && result.aiData) {
                    const sourceContent = {
                        title: result.aiData[`Title_${langCode}`] || '',
                        description: result.aiData[`Description_${langCode}`] || '',
                        keywords: result.aiData[`Keywords_${langCode}`] || '',
                        category: result.aiData[`Category_${langCode}`] || ''
                    };
                    // Run translation in background (don't await)
                    translateWebsiteToAllLanguages(website.id, sourceContent, langCode)
                        .then(() => loadData())
                        .catch(err => console.error('Background translation error:', err));
                }
            } else {
                alert(result.message);
            }
        } catch (error) {
            console.error('Error analyzing website:', error);
        } finally {
            setAnalyzingId(null);
        }
    };

    const handleCopyUrl = (website) => {
        const publicUrl = `${window.location.origin}/website/${website.uuid}`;
        navigator.clipboard.writeText(publicUrl);
        setCopiedUrl(website.id);
        setTimeout(() => setCopiedUrl(null), 2000);
    };

    const handleViewDetail = (website) => {
        setSelectedWebsiteId(website.id);
    };

    // Analyze URL in the form and fill fields (for new websites)
    const handleAnalyzeInForm = async () => {
        if (!formData.domain.trim()) return;

        const url = `${formData.protocol}${formData.domain.trim()}`;
        const langCode = language?.substring(0, 2) || 'fr';

        setAnalyzingId('form');
        try {
            // Import directly to avoid circular dependency
            const { analyzeWebsite } = await import('../../services/geminiService');
            const result = await analyzeWebsite(url, langCode);

            if (result) {
                // Fill form fields with AI results
                setFormData(prev => ({
                    ...prev,
                    editTitle: result[`Title_${langCode}`] || prev.editTitle,
                    editDescription: result[`Description_${langCode}`] || prev.editDescription,
                    editKeywords: result[`Keywords_${langCode}`] || prev.editKeywords,
                    editCategory: result[`Category_${langCode}`] || prev.editCategory
                }));
            }
        } catch (error) {
            console.error('Error analyzing website in form:', error);
            alert(t.websites?.analyzeError || 'Erreur lors de l\'analyse du site');
        } finally {
            setAnalyzingId(null);
        }
    };

    const handleGenerateSnapshot = async (website) => {
        setGeneratingSnapshotId(website.id);
        try {
            const result = await generateSnapshot(website.id, website.url);
            if (result.success) {
                await loadData();
            } else {
                alert(result.message);
            }
        } catch (error) {
            console.error('Error generating snapshot:', error);
        } finally {
            setGeneratingSnapshotId(null);
        }
    };

    const parseSocialLinks = (linksJson) => {
        try {
            return JSON.parse(linksJson || '{}');
        } catch {
            return {};
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-cabaza-purple" />
            </div>
        );
    }

    // Show detail view if a website is selected
    if (selectedWebsiteId) {
        return (
            <div className="max-w-6xl mx-auto px-4 py-6">
                <WebsiteDetail
                    websiteId={selectedWebsiteId}
                    websites={websites}
                    onBack={() => setSelectedWebsiteId(null)}
                    onEdit={(website) => {
                        setSelectedWebsiteId(null);
                        handleOpenForm(website);
                    }}
                    onGenerateSnapshot={handleGenerateSnapshot}
                />
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto px-4 py-6">
            <PageHeader
                icon={Globe}
                title={t.websites?.title || 'Mes sites web'}
                subtitle={t.websites?.subtitle || 'Enregistrez vos sites web et générez automatiquement une page de présentation'}
            />

            {/* Add button */}
            <div className="mb-6">
                <Button onClick={() => handleOpenForm()} icon={Plus}>
                    {t.websites?.addNew || 'Ajouter un site web'}
                </Button>
            </div>

            {/* Websites list */}
            {websites.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                    <Globe className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {t.websites?.noWebsites || 'Aucun site web'}
                    </h3>
                    <p className="text-gray-500 mb-4">
                        {t.websites?.noWebsitesDesc || 'Ajoutez votre premier site web pour générer une page de présentation automatique'}
                    </p>
                    <Button onClick={() => handleOpenForm()} icon={Plus}>
                        {t.websites?.addFirst || 'Ajouter mon premier site'}
                    </Button>
                </div>
            ) : (
                <div className="space-y-4">
                    {websites.map(website => {
                        const socialLinks = parseSocialLinks(website.socialLinks);
                        const langCode = language?.substring(0, 2) || 'fr';
                        // Get multilingual fields in current language
                        const displayTitle = website[`title_${langCode}`] || website[`category_${langCode}`] || website.title || new URL(website.url).hostname;
                        const displayDescription = website[`description_${langCode}`] || '';
                        const displayCategory = website[`category_${langCode}`] || '';
                        const displayKeywords = website[`keywords_${langCode}`] || '';

                        return (
                            <div key={website.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                                <div className="p-5">
                                    <div className="flex items-start justify-between gap-4">
                                        {/* Snapshot thumbnail */}
                                        <div className="flex-shrink-0 relative group">
                                            {website.snapshotPath ? (
                                                <div className="relative">
                                                    <img
                                                        src={website.snapshotPath}
                                                        alt={displayTitle}
                                                        className="w-32 h-24 object-cover rounded-lg border border-gray-200"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextSibling.style.display = 'flex';
                                                        }}
                                                    />
                                                    <div className="w-32 h-24 rounded-lg bg-gray-100 items-center justify-center hidden">
                                                        <Globe className="w-8 h-8 text-gray-400" />
                                                    </div>
                                                    <button
                                                        onClick={() => handleGenerateSnapshot(website)}
                                                        disabled={generatingSnapshotId === website.id}
                                                        className="absolute bottom-1 right-1 p-1.5 bg-white/90 rounded-lg shadow opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
                                                        title={t.websites?.refreshSnapshot || 'Actualiser le snapshot'}
                                                    >
                                                        {generatingSnapshotId === website.id ? (
                                                            <Loader2 className="w-4 h-4 animate-spin text-cabaza-purple" />
                                                        ) : (
                                                            <RefreshCw className="w-4 h-4 text-gray-600" />
                                                        )}
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => handleGenerateSnapshot(website)}
                                                    disabled={generatingSnapshotId === website.id}
                                                    className="w-32 h-24 rounded-lg bg-gray-100 border-2 border-dashed border-gray-300 flex flex-col items-center justify-center gap-1 hover:bg-gray-50 hover:border-cabaza-purple transition-colors"
                                                >
                                                    {generatingSnapshotId === website.id ? (
                                                        <Loader2 className="w-6 h-6 animate-spin text-cabaza-purple" />
                                                    ) : (
                                                        <>
                                                            <Globe className="w-6 h-6 text-gray-400" />
                                                            <span className="text-xs text-gray-500">{t.websites?.generateSnapshot || 'Générer'}</span>
                                                        </>
                                                    )}
                                                </button>
                                            )}
                                        </div>

                                        {/* Left content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-3 mb-2">
                                                <div>
                                                    <h3 className="font-semibold text-gray-900 truncate">{displayTitle}</h3>
                                                    <a
                                                        href={website.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-sm text-cabaza-purple hover:underline flex items-center gap-1"
                                                    >
                                                        {website.url}
                                                        <ExternalLink className="w-3 h-3" />
                                                    </a>
                                                </div>
                                            </div>

                                            {/* AI Description */}
                                            {displayDescription && (
                                                <p className="text-sm text-gray-600 mb-3 line-clamp-3">{displayDescription}</p>
                                            )}

                                            {/* Extracted info */}
                                            {website.processed && (
                                                <div className="flex flex-wrap gap-2 mb-3">
                                                    {displayCategory && (
                                                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded-full text-xs">
                                                            <Tag className="w-3 h-3" />
                                                            {displayCategory}
                                                        </span>
                                                    )}
                                                    {website.contactEmail && (
                                                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs">
                                                            <Mail className="w-3 h-3" />
                                                            {website.contactEmail}
                                                        </span>
                                                    )}
                                                    {website.contactPhone && (
                                                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs">
                                                            <Phone className="w-3 h-3" />
                                                            {website.contactPhone}
                                                        </span>
                                                    )}
                                                    {website.address && (
                                                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-50 text-orange-700 rounded-full text-xs">
                                                            <MapPin className="w-3 h-3" />
                                                            {website.address}
                                                        </span>
                                                    )}
                                                </div>
                                            )}

                                            {/* Social links */}
                                            {Object.keys(socialLinks).some(k => socialLinks[k]) && (
                                                <div className="flex gap-2 mb-3">
                                                    {socialLinks.facebook && (
                                                        <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="p-1.5 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200">
                                                            <Facebook className="w-4 h-4" />
                                                        </a>
                                                    )}
                                                    {socialLinks.linkedin && (
                                                        <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="p-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200">
                                                            <Linkedin className="w-4 h-4" />
                                                        </a>
                                                    )}
                                                    {socialLinks.instagram && (
                                                        <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="p-1.5 bg-pink-100 text-pink-600 rounded-lg hover:bg-pink-200">
                                                            <Instagram className="w-4 h-4" />
                                                        </a>
                                                    )}
                                                    {socialLinks.twitter && (
                                                        <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="p-1.5 bg-sky-100 text-sky-600 rounded-lg hover:bg-sky-200">
                                                            <Twitter className="w-4 h-4" />
                                                        </a>
                                                    )}
                                                    {socialLinks.youtube && (
                                                        <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="p-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200">
                                                            <Youtube className="w-4 h-4" />
                                                        </a>
                                                    )}
                                                </div>
                                            )}

                                            {/* Stats */}
                                            <div className="flex items-center gap-4 text-sm text-gray-500">
                                                <span className="flex items-center gap-1">
                                                    <Eye className="w-4 h-4" />
                                                    {website.viewCount || 0} {t.websites?.views || 'vues'}
                                                </span>
                                                <span className={`flex items-center gap-1 ${website.isPublic ? 'text-green-600' : 'text-gray-400'}`}>
                                                    {website.isPublic ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                                                    {website.isPublic ? (t.websites?.public || 'Public') : (t.websites?.private || 'Privé')}
                                                </span>
                                                {website.processed && (
                                                    <span className="flex items-center gap-1 text-purple-600">
                                                        <Sparkles className="w-4 h-4" />
                                                        {t.websites?.aiAnalyzed || 'Analysé par IA'}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Right actions */}
                                        <div className="flex flex-col gap-2">
                                            {/* View detail page */}
                                            <Button
                                                size="sm"
                                                variant="secondary"
                                                onClick={() => handleViewDetail(website)}
                                                icon={Eye}
                                            >
                                                {t.websites?.viewDetails || 'Voir détails'}
                                            </Button>

                                            {/* Copy public URL */}
                                            {website.isPublic && (
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => handleCopyUrl(website)}
                                                    icon={copiedUrl === website.id ? Check : Copy}
                                                >
                                                    {copiedUrl === website.id ? (t.websites?.copied || 'Copié!') : (t.websites?.copyUrl || 'Copier l\'URL')}
                                                </Button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Bottom actions */}
                                    <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                                        <button
                                            onClick={() => handleTogglePublic(website)}
                                            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                                            title={website.isPublic ? 'Rendre privé' : 'Rendre public'}
                                        >
                                            {website.isPublic ? (
                                                <ToggleRight className="w-5 h-5 text-green-600" />
                                            ) : (
                                                <ToggleLeft className="w-5 h-5 text-gray-400" />
                                            )}
                                        </button>
                                        <button
                                            onClick={() => handleOpenForm(website)}
                                            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                                            title="Modifier"
                                        >
                                            <Edit className="w-5 h-5 text-gray-600" />
                                        </button>
                                        <button
                                            onClick={() => setDeleteConfirm(website)}
                                            className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                                            title="Supprimer"
                                        >
                                            <Trash2 className="w-5 h-5 text-red-500" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Form Modal */}
            <Modal
                isOpen={showForm}
                onClose={handleCloseForm}
                title={editingWebsite ? (t.websites?.editWebsite || 'Modifier le site web') : (t.websites?.newWebsite || 'Nouveau site web')}
                size="lg"
            >
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Content policy warning */}
                    <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="font-medium text-amber-800">
                                {t.websites?.contentPolicy || 'Politique de contenu'}
                            </p>
                            <p className="text-sm text-amber-700 mt-1">
                                {t.websites?.contentPolicyDesc || 'Les sites pour adultes, pornographiques, de jeux d\'argent illégaux ou promouvant la haine sont strictement interdits. Les URLs sont automatiquement vérifiées par notre système de modération IA. Tout contenu inapproprié sera rejeté et peut entraîner la suspension de votre compte.'}
                            </p>
                        </div>
                    </div>

                    {/* Moderation error */}
                    {moderationError && (
                        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                            <ShieldAlert className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="font-medium text-red-800">
                                    {t.websites?.contentRejected || 'Site rejeté'}
                                </p>
                                <p className="text-sm text-red-700 mt-1">{moderationError}</p>
                            </div>
                        </div>
                    )}

                    {/* URL with Protocol Selector and AI Analyze button */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.websites?.websiteUrl || 'URL du site web'} *
                        </label>
                        <div className="flex items-stretch gap-2">
                            <div className="flex flex-1">
                                <CustomDropdown
                                    value={formData.protocol}
                                    onChange={(protocol) => setFormData(prev => ({ ...prev, protocol }))}
                                    options={[
                                        { value: 'https://', label: 'https://' },
                                        { value: 'http://', label: 'http://' }
                                    ]}
                                    className="px-3 py-2 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm rounded-l-lg focus:outline-none"
                                    disabled={!!editingWebsite}
                                />
                                <input
                                    type="text"
                                    value={formData.domain}
                                    onChange={(e) => setFormData(prev => ({ ...prev, domain: e.target.value }))}
                                    className="flex-1 px-4 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                                    placeholder="www.exemple.com"
                                    required
                                    disabled={!!editingWebsite}
                                />
                            </div>
                            {/* AI Analyze button (only for new) */}
                            {!editingWebsite && isGeminiAvailable() && (
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={handleAnalyzeInForm}
                                    disabled={!formData.domain.trim() || analyzingId === 'form'}
                                    icon={analyzingId === 'form' ? Loader2 : Sparkles}
                                    className={analyzingId === 'form' ? 'animate-pulse' : ''}
                                >
                                    {analyzingId === 'form'
                                        ? (t.websites?.analyzing || 'Analyse...')
                                        : (t.websites?.analyzeAI || 'Analyser IA')
                                    }
                                </Button>
                            )}
                        </div>
                        {!editingWebsite && (
                            <p className="text-xs text-gray-500 mt-1">
                                {t.websites?.urlHintWithAI || 'Entrez l\'URL puis cliquez sur "Analyser IA" pour remplir automatiquement les champs'}
                            </p>
                        )}
                    </div>

                    {/* Title field */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.websites?.titleField || 'Titre'} ({language?.substring(0, 2)?.toUpperCase() || 'FR'}) *
                        </label>
                        <input
                            type="text"
                            value={formData.editTitle}
                            onChange={(e) => setFormData(prev => ({ ...prev, editTitle: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                            placeholder={t.websites?.titlePlaceholder || 'Titre accrocheur décrivant l\'activité'}
                            required
                        />
                    </div>

                    {/* Description field */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.websites?.descriptionField || 'Description'} ({language?.substring(0, 2)?.toUpperCase() || 'FR'})
                        </label>
                        <textarea
                            value={formData.editDescription}
                            onChange={(e) => setFormData(prev => ({ ...prev, editDescription: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                            rows={4}
                            placeholder={t.websites?.descriptionPlaceholder || 'Description du site web'}
                        />
                    </div>

                    {/* Keywords field */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.websites?.keywordsField || 'Mots-clés'} ({language?.substring(0, 2)?.toUpperCase() || 'FR'})
                        </label>
                        <input
                            type="text"
                            value={formData.editKeywords}
                            onChange={(e) => setFormData(prev => ({ ...prev, editKeywords: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                            placeholder={t.websites?.keywordsPlaceholder || 'Mots-clés séparés par des virgules'}
                        />
                    </div>

                    {/* Category field */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t.websites?.categoryField || 'Catégorie'} ({language?.substring(0, 2)?.toUpperCase() || 'FR'})
                        </label>
                        <input
                            type="text"
                            value={formData.editCategory}
                            onChange={(e) => setFormData(prev => ({ ...prev, editCategory: e.target.value }))}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cabaza-purple focus:border-transparent"
                            placeholder={t.websites?.categoryPlaceholder || 'Ex: Restaurant, Agence web, etc.'}
                        />
                    </div>

                    {/* Reanalyze button (only for editing) */}
                    {editingWebsite && isGeminiAvailable() && (
                        <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                            <div>
                                <p className="font-medium text-purple-900">{t.websites?.reanalyzeTitle || 'Régénérer avec l\'IA'}</p>
                                <p className="text-sm text-purple-700">
                                    {t.websites?.reanalyzeDesc || `Réanalyser le site et regénérer le contenu en ${language?.substring(0, 2)?.toUpperCase() || 'FR'}`}
                                </p>
                            </div>
                            <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                onClick={handleAnalyzeInForm}
                                disabled={analyzingId === 'form'}
                                icon={analyzingId === 'form' ? Loader2 : Sparkles}
                                className={analyzingId === 'form' ? 'animate-pulse' : ''}
                            >
                                {analyzingId === 'form'
                                    ? (t.websites?.analyzing || 'Analyse...')
                                    : (t.websites?.reanalyze || 'Réanalyser')
                                }
                            </Button>
                        </div>
                    )}

                    {/* Public toggle */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                            <p className="font-medium text-gray-900">{t.websites?.publicPage || 'Page publique'}</p>
                            <p className="text-sm text-gray-500">{t.websites?.publicPageDesc || 'Rendre cette page visible publiquement'}</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, isPublic: !prev.isPublic }))}
                            className={`relative w-12 h-6 rounded-full transition-colors ${formData.isPublic ? 'bg-green-500' : 'bg-gray-300'}`}
                        >
                            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${formData.isPublic ? 'translate-x-6' : ''}`} />
                        </button>
                    </div>

                    {/* Submit buttons */}
                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <Button type="button" variant="secondary" onClick={handleCloseForm}>
                            {t.common?.cancel || 'Annuler'}
                        </Button>
                        <Button type="submit" icon={Save} disabled={saving || checkingContent}>
                            {saving || checkingContent ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    {checkingContent
                                        ? (t.websites?.checkingContent || 'Vérification du contenu...')
                                        : (t.common?.saving || 'Enregistrement...')
                                    }
                                </>
                            ) : (
                                t.common?.save || 'Enregistrer'
                            )}
                        </Button>
                    </div>
                </form>
            </Modal>

            {/* Delete confirmation */}
            <ConfirmModal
                isOpen={!!deleteConfirm}
                onClose={() => setDeleteConfirm(null)}
                onConfirm={handleDelete}
                title={t.websites?.deleteTitle || 'Supprimer le site web'}
                message={t.websites?.deleteMessage || 'Êtes-vous sûr de vouloir supprimer ce site web ? La page publique associée sera également supprimée.'}
                confirmText={t.common?.delete || 'Supprimer'}
                cancelText={t.common?.cancel || 'Annuler'}
                variant="danger"
            />
        </div>
    );
};

export default WebsitesPage;
